import React, { useState } from 'react';

import { Alert, Button, Col, Card, Form, InputGroup, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';

import {
  backgroundGrey1,
  greyBorder,
  lightTransparentBlue,
  shadowMedium,
  shadowSoftBlue,
  slateGrey,
} from 'lib/css/colors';
import Container from 'components/shared/Container';
import { DEFAULT_PAGE_SIZE } from 'lib/constants';
import PaginationComponent from 'components/shared/PaginationComponent';
import SupplierBrandTable from 'components/supplier_brand_management/SupplierBrandTable';
import { SUPPLIER_BRAND_MANAGEMENT_RECORDS } from 'lib/networking/endpoints';
import useGet from 'lib/hooks/useGet';

function SupplierBrandManagementView() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: { data: { records = [], total_count = 0 } = {} } = {},
    loading,
    error,
    refetch,
  } = useGet(SUPPLIER_BRAND_MANAGEMENT_RECORDS, {
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
    ...(searchTerm && { search: searchTerm }),
  });

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = size => {
    setItemsPerPage(size);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  return (
    <Container>
      <Row>
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom-0 pb-0">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0 text-dark">Supplier Brand Management</h4>
                <div className={css(styles.statsText)}>
                  {loading ? (
                    <span>Loading...</span>
                  ) : (
                    <span>
                      <strong>{total_count.toLocaleString()}</strong> total records
                      {searchTerm && (
                        <span className="text-primary">
                          {' '}
                          (filtered by &ldquo;{searchTerm}&rdquo;)
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              <div className={css(styles.searchContainer)}>
                <Row className="align-items-center">
                  <Col md={8}>
                    <Form.Label className="mb-2 fw-semibold text-dark">
                      <i className="fas fa-search me-2"></i>
                      Search Records
                    </Form.Label>
                    <div className="d-flex">
                      <InputGroup className={css(styles.searchInputGroup)}>
                        <Form.Control
                          type="text"
                          placeholder="Search by supplier name, brand name, originating names..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                          className={css(styles.searchInput)}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={handleClearSearch}
                          className={css(styles.clearButton)}
                          title="Clear search"
                        >
                          Clear search
                        </Button>
                      </InputGroup>
                    </div>
                    <Form.Text className="text-muted mt-1">
                      <i className="fas fa-info-circle me-1"></i>
                      Search results update automatically as you type
                    </Form.Text>
                  </Col>
                </Row>
              </div>

              {error && (
                <Alert variant="danger" className="m-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Error loading supplier brand records: {error.message}
                </Alert>
              )}

              {!loading && !error && total_count > 0 && (
                <div className="d-flex justify-content-end p-3 border-top bg-light">
                  <PaginationComponent
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    totalItems={total_count}
                    setCurrentPage={handlePageChange}
                    setItemsPerPage={handlePageSizeChange}
                  />
                </div>
              )}

              <div className="px-3">
                <SupplierBrandTable records={records} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

const styles = StyleSheet.create({
  searchContainer: {
    backgroundColor: backgroundGrey1,
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  searchInputGroup: {
    boxShadow: `0 2px 4px ${shadowMedium}`,
  },
  searchInput: {
    border: `1px solid ${greyBorder}`,
    borderRadius: '6px 0 0 6px',
    fontSize: '14px',
    ':focus': {
      borderColor: lightTransparentBlue,
      boxShadow: `0 0 0 0.2rem ${shadowSoftBlue}`,
    },
    marginRight: '20px',
  },
  clearButton: {
    borderRadius: '6px',
  },
  statsText: {
    fontSize: '14px',
    color: slateGrey,
  },
});

export default SupplierBrandManagementView;
